import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './CMS.css';

const CMS = () => {
  const [trackingData, setTrackingData] = useState([]);
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPage, setFilterPage] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [ipSearch, setIpSearch] = useState('');
  const navigate = useNavigate();

  const fetchTrackingData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filterPage) params.append('page', filterPage);
      if (filterAction) params.append('action', filterAction);
      params.append('limit', '1000');

      console.log('🔵 [CMS] Fetching tracking data:', params.toString());
      const response = await fetch(`${API_BASE_URL}/tracking/data?${params.toString()}`);
      const data = await response.json();

      console.log('🔵 [CMS] Received data:', data.success, 'Records:', data.data?.length || 0);

      if (data.success) {
        setTrackingData(data.data || []);
        setStats(data.stats || {});
        console.log('✅ [CMS] Data loaded:', data.data?.length || 0, 'records');
      } else {
        console.error('❌ [CMS] Failed to fetch data:', data);
      }
    } catch (error) {
      console.error('❌ [CMS] Error fetching tracking data:', error);
    } finally {
      setLoading(false);
    }
  }, [filterPage, filterAction]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/tracking/analytics`);
      const data = await response.json();

      if (data.success) {
        setAnalytics(data.analytics);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, []);

  useEffect(() => {
    // Check if user is logged in
    if (!localStorage.getItem('isAdmin')) {
      navigate('/login');
      return;
    }

    fetchTrackingData();
    fetchAnalytics();
  }, [navigate, filterPage, filterAction, fetchTrackingData, fetchAnalytics]);

  const handleLogout = () => {
    localStorage.removeItem('isAdmin');
    navigate('/login');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // CSV Export function
  const exportToCSV = (data, filename, headers) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Create CSV content
    let csvContent = '';
    
    // Add headers
    if (headers && headers.length > 0) {
      csvContent += headers.map(h => `"${h}"`).join(',') + '\n';
    }

    // Add data rows
    data.forEach(row => {
      const values = headers.map(header => {
        let value = row[header] || '';
        // Handle nested objects
        if (typeof value === 'object' && value !== null) {
          value = JSON.stringify(value);
        }
        // Escape quotes and wrap in quotes
        value = String(value).replace(/"/g, '""');
        return `"${value}"`;
      });
      csvContent += values.join(',') + '\n';
    });

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export IP Tracking data
  const exportIPTracking = () => {
    const filteredData = ipTrackingData.filter(ipData => {
      if (filterPage && !ipData.pages.includes(filterPage)) {
        return false;
      }
      if (ipSearch) {
        const searchIP = ipSearch.toLowerCase();
        if (!ipData.ip.toLowerCase().includes(searchIP)) {
          return false;
        }
      }
      return true;
    });

    const headers = ['IP Address', 'City', 'Region', 'Country', 'ISP', 'Organization', 'Pages Visited', 'Visit Count', 'First Visit', 'Last Visit'];
    const csvData = filteredData.map(ipData => ({
      'IP Address': ipData.ip,
      'City': ipData.locationData?.city || 'N/A',
      'Region': ipData.locationData?.region || 'N/A',
      'Country': ipData.locationData?.country || 'N/A',
      'ISP': ipData.locationData?.isp || 'N/A',
      'Organization': ipData.locationData?.org || 'N/A',
      'Pages Visited': ipData.pages.map(p => p === '/' ? 'मतदार शोध' : p === '/voting' ? 'मतदान यंत्र' : p).join('; '),
      'Visit Count': ipData.visitCount,
      'First Visit': formatDate(ipData.firstVisit),
      'Last Visit': formatDate(ipData.lastVisit)
    }));

    exportToCSV(csvData, `ip-tracking-${new Date().toISOString().split('T')[0]}.csv`, headers);
  };

  // Export Vote Analytics
  const exportVoteAnalytics = () => {
    const headers = ['ID', 'Ward', 'Division', 'Candidate Name', 'Serial No', 'Party', 'IP Address', 'Timestamp'];
    const csvData = voteClicks.map(row => {
      const voteDetails = parseVoteDetails(row.details);
      return {
        'ID': row.id,
        'Ward': `Ward ${voteDetails?.wardNumber || 'N/A'}`,
        'Division': voteDetails?.wardDivision || 'N/A',
        'Candidate Name': voteDetails?.candidateName || 'N/A',
        'Serial No': voteDetails?.candidateSerialNumber || 'N/A',
        'Party': voteDetails?.party || 'N/A',
        'IP Address': row.ip_address || 'N/A',
        'Timestamp': formatDate(row.timestamp)
      };
    });

    exportToCSV(csvData, `vote-analytics-${new Date().toISOString().split('T')[0]}.csv`, headers);
  };

  // Export All Details
  const exportAllDetails = () => {
    const headers = ['ID', 'Page', 'Action', 'IP Address', 'Location', 'Details', 'User Agent', 'Timestamp'];
    const csvData = trackingData.map(row => ({
      'ID': row.id,
      'Page': row.page,
      'Action': row.action,
      'IP Address': row.ip_address || 'N/A',
      'Location': parseLocation(row.location),
      'Details': parseDetails(row.details),
      'User Agent': row.user_agent || 'N/A',
      'Timestamp': formatDate(row.timestamp)
    }));

    exportToCSV(csvData, `all-details-${new Date().toISOString().split('T')[0]}.csv`, headers);
  };

  const parseLocation = (locationStr) => {
    if (!locationStr) return 'N/A';
    try {
      const loc = JSON.parse(locationStr);
      const parts = [];
      if (loc.city) parts.push(loc.city);
      if (loc.region) parts.push(loc.region);
      if (loc.country) parts.push(loc.country);
      if (loc.isp) parts.push(`[ISP: ${loc.isp}]`);
      if (loc.org) parts.push(`[Org: ${loc.org}]`);
      return parts.length > 0 ? parts.join(', ') : 'N/A';
    } catch {
      return locationStr;
    }
  };

  const parseDetails = (detailsStr) => {
    if (!detailsStr) return 'N/A';
    try {
      const details = JSON.parse(detailsStr);
      
      // Format search details
      if (details.searchType) {
        let searchInfo = `Search Type: ${details.searchType === 'name' ? 'Name' : 'EPIC'}\n`;
        if (details.firstName) searchInfo += `First Name: ${details.firstName}\n`;
        if (details.middleName) searchInfo += `Middle Name: ${details.middleName}\n`;
        if (details.surname) searchInfo += `Surname: ${details.surname}\n`;
        if (details.epic) searchInfo += `EPIC: ${details.epic}\n`;
        return searchInfo;
      }
      
      // Format WhatsApp share details
      if (details.voterName || details.epicId) {
        let shareInfo = `Shared Voter:\n`;
        if (details.voterName) shareInfo += `Name: ${details.voterName}\n`;
        if (details.voterNameMarathi) shareInfo += `Name (Marathi): ${details.voterNameMarathi}\n`;
        if (details.epicId) shareInfo += `EPIC ID: ${details.epicId}\n`;
        if (details.age) shareInfo += `Age: ${details.age}\n`;
        if (details.partNo) shareInfo += `Part No: ${details.partNo}\n`;
        if (details.voterNo) shareInfo += `Voter No: ${details.voterNo}\n`;
        if (details.boothAddress) shareInfo += `Booth: ${details.boothAddress}\n`;
        if (details.hasFamilyMembers) {
          shareInfo += `Family Members: ${details.familyMemberCount || 'Yes'}\n`;
        }
        return shareInfo;
      }
      
      return JSON.stringify(details, null, 2);
    } catch {
      return detailsStr;
    }
  };

  const parseVoteDetails = (detailsStr) => {
    if (!detailsStr) return null;
    try {
      return JSON.parse(detailsStr);
    } catch {
      return null;
    }
  };

  const getVoteButtonClicks = () => {
    return trackingData.filter(row => {
      if (!row.action) return false;
      const action = row.action.toLowerCase();
      return action.includes('vote') || action === 'click_vote';
    });
  };

  if (loading) {
    return (
      <div className="cms-container">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  const voteClicks = getVoteButtonClicks();

  // Get unique IPs with their visit details
  const getIPTrackingData = () => {
    const ipMap = {};
    
    trackingData.forEach(row => {
      const ip = row.ip_address || 'unknown';
      if (!ipMap[ip]) {
        const location = parseLocation(row.location);
        let locData = null;
        try {
          locData = row.location ? JSON.parse(row.location) : null;
        } catch (e) {
          // Ignore parse errors
        }
        
        ipMap[ip] = {
          ip,
          location: location,
          locationData: locData,
          pages: new Set(),
          actions: new Set(),
          firstVisit: row.timestamp,
          lastVisit: row.timestamp,
          visitCount: 0,
          records: []
        };
      }
      
      ipMap[ip].pages.add(row.page);
      ipMap[ip].actions.add(row.action);
      ipMap[ip].visitCount++;
      ipMap[ip].records.push(row);
      
      const rowTime = new Date(row.timestamp);
      const firstTime = new Date(ipMap[ip].firstVisit);
      const lastTime = new Date(ipMap[ip].lastVisit);
      
      if (rowTime < firstTime) {
        ipMap[ip].firstVisit = row.timestamp;
      }
      if (rowTime > lastTime) {
        ipMap[ip].lastVisit = row.timestamp;
      }
    });
    
    return Object.values(ipMap).map(ipData => ({
      ...ipData,
      pages: Array.from(ipData.pages),
      actions: Array.from(ipData.actions)
    })).sort((a, b) => new Date(b.lastVisit) - new Date(a.lastVisit));
  };

  // Calculate IP tracking data
  const ipTrackingData = getIPTrackingData();

  return (
    <div className="cms-container">
      <div className="cms-header">
        <h1>CMS Dashboard - Analytics & Tracking</h1>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>

      <div className="tabs-section">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'iptracking' ? 'active' : ''}`}
          onClick={() => setActiveTab('iptracking')}
        >
          IP Tracking
        </button>
        <button 
          className={`tab-btn ${activeTab === 'votes' ? 'active' : ''}`}
          onClick={() => setActiveTab('votes')}
        >
          Vote Analytics ({voteClicks.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          All Details
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          <div className="stats-section">
            <div className="stat-card">
              <div className="stat-value">{stats.total_visits || 0}</div>
              <div className="stat-label">Total Visits</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.total_hits || stats.total_visits || 0}</div>
              <div className="stat-label">Total Hits</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.pages_visited || 0}</div>
              <div className="stat-label">Pages Visited</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{voteClicks.length}</div>
              <div className="stat-label">Total Votes</div>
            </div>
          </div>

          {analytics && (
            <div className="analytics-section">
              <div className="analytics-card">
                <h3>Page Visits Breakdown</h3>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Total Hits</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.pageStats.map((stat, idx) => (
                      <tr key={idx}>
                        <td>{stat.page}</td>
                        <td>{stat.visits || stat.total_hits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="analytics-card">
                <h3>Action Breakdown</h3>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.actionStats.map((stat, idx) => (
                      <tr key={idx}>
                        <td>{stat.action}</td>
                        <td>{stat.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="analytics-card">
                <h3>Top IPs (All Hits)</h3>
                <table className="analytics-table">
                  <thead>
                    <tr>
                      <th>IP Address</th>
                      <th>Total Hits</th>
                      <th>Pages Visited</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.topIPs.map((ip, idx) => (
                      <tr key={idx}>
                        <td>{ip.ip_address}</td>
                        <td>{ip.visit_count}</td>
                        <td>{ip.pages_visited}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'iptracking' && (
        <div className="analytics-section">
          <div className="analytics-card full-width">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>IP Tracking - All Visitors</h3>
              <button onClick={exportIPTracking} className="export-btn">
                📥 Export to CSV
              </button>
            </div>
            
            <div className="filters-section">
              <select
                value={filterPage}
                onChange={(e) => setFilterPage(e.target.value)}
                className="filter-select"
              >
                <option value="">All Pages</option>
                <option value="/">🔍 मतदार शोध / Voter Search</option>
                <option value="/voting">🗳️ डिजिटल मतदान यंत्र / Voting Machine</option>
              </select>
              <input
                type="text"
                placeholder="Search by IP address..."
                className="filter-input"
                value={ipSearch}
                onChange={(e) => setIpSearch(e.target.value)}
              />
              <button onClick={() => { fetchTrackingData(); fetchAnalytics(); }} className="refresh-btn">
                Refresh
              </button>
            </div>

            <div className="table-container">
              <table className="tracking-table">
                <thead>
                  <tr>
                    <th>IP Address</th>
                    <th>Location</th>
                    <th>City</th>
                    <th>Region</th>
                    <th>Country</th>
                    <th>ISP</th>
                    <th>Organization</th>
                    <th>Pages Visited</th>
                    <th>Visit Count</th>
                    <th>First Visit</th>
                    <th>Last Visit</th>
                  </tr>
                </thead>
                <tbody>
                  {ipTrackingData.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="no-data">No tracking data available</td>
                    </tr>
                  ) : (
                    ipTrackingData
                      .filter(ipData => {
                        // Filter by page if selected
                        if (filterPage && !ipData.pages.includes(filterPage)) {
                          return false;
                        }
                        // Filter by IP search if entered
                        if (ipSearch) {
                          const searchIP = ipSearch.toLowerCase();
                          if (!ipData.ip.toLowerCase().includes(searchIP)) {
                            return false;
                          }
                        }
                        return true;
                      })
                      .map((ipData) => (
                        <tr key={ipData.ip}>
                          <td><strong>{ipData.ip}</strong></td>
                          <td>
                            {ipData.locationData ? (
                              <div>
                                {ipData.locationData.city && <div>{ipData.locationData.city}</div>}
                                {ipData.locationData.region && <div>{ipData.locationData.region}</div>}
                                {ipData.locationData.country && <div><strong>{ipData.locationData.country}</strong></div>}
                              </div>
                            ) : (
                              ipData.location || 'N/A'
                            )}
                          </td>
                          <td>{ipData.locationData?.city || 'N/A'}</td>
                          <td>{ipData.locationData?.region || 'N/A'}</td>
                          <td>{ipData.locationData?.country || 'N/A'}</td>
                          <td>{ipData.locationData?.isp || 'N/A'}</td>
                          <td>{ipData.locationData?.org || 'N/A'}</td>
                          <td>
                            <div className="pages-list">
                              {ipData.pages.length === 0 ? (
                                <span className="page-badge">No pages</span>
                              ) : (
                                <>
                                  {ipData.pages.includes('/') && (
                                    <span key="/" className="page-badge search">
                                      🔍 मतदार शोध
                                    </span>
                                  )}
                                  {ipData.pages.includes('/voting') && (
                                    <span key="/voting" className="page-badge voting">
                                      🗳️ मतदान यंत्र
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          <td><strong>{ipData.visitCount}</strong></td>
                          <td>{formatDate(ipData.firstVisit)}</td>
                          <td>{formatDate(ipData.lastVisit)}</td>
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'votes' && (
        <div className="votes-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h2>Vote Button Click Analytics</h2>
            <button onClick={exportVoteAnalytics} className="export-btn">
              📥 Export to CSV
            </button>
          </div>
          <div className="table-container">
            <table className="tracking-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Ward</th>
                  <th>Division</th>
                  <th>Candidate Name</th>
                  <th>Serial No</th>
                  <th>Party</th>
                  <th>IP Address</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {voteClicks.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="no-data">No vote clicks recorded</td>
                  </tr>
                ) : (
                  voteClicks.map((row) => {
                    const voteDetails = parseVoteDetails(row.details);
                    return (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td>Ward {voteDetails?.wardNumber || 'N/A'}</td>
                        <td>{voteDetails?.wardDivision || 'N/A'}</td>
                        <td>{voteDetails?.candidateName || 'N/A'}</td>
                        <td>{voteDetails?.candidateSerialNumber || 'N/A'}</td>
                        <td>{voteDetails?.party || 'N/A'}</td>
                        <td>{row.ip_address || 'N/A'}</td>
                        <td>{formatDate(row.timestamp)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'details' && (
        <>
          <div className="filters-section">
            <select
              value={filterPage}
              onChange={(e) => setFilterPage(e.target.value)}
              className="filter-select"
            >
              <option value="">All Pages</option>
              <option value="/">Search Page</option>
              <option value="/voting">Voting Machine</option>
            </select>
            <select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              className="filter-select"
            >
              <option value="">All Actions</option>
              <option value="visit">Visits</option>
              <option value="click_search">Search (मतदार शोध)</option>
              <option value="click_family_search">Family Search</option>
              <option value="click_whatsapp_share">WhatsApp Share</option>
              <option value="click_vote_button_click">Vote Clicks</option>
            </select>
              <button onClick={() => { fetchTrackingData(); fetchAnalytics(); }} className="refresh-btn">
                Refresh
              </button>
              <button onClick={exportAllDetails} className="export-btn">
                📥 Export to CSV
              </button>
            </div>

          <div className="table-container">
        <table className="tracking-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Page</th>
              <th>Action</th>
              <th>IP Address</th>
              <th>Location</th>
              <th>Details</th>
              <th>User Agent</th>
              <th>Timestamp</th>
            </tr>
          </thead>
          <tbody>
            {trackingData.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">No tracking data available</td>
              </tr>
            ) : (
              trackingData.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.page}</td>
                  <td>
                    <span className={`action-badge ${row.action}`}>
                      {row.action}
                    </span>
                  </td>
                  <td>{row.ip_address || 'N/A'}</td>
                  <td>{parseLocation(row.location)}</td>
                  <td className="details-cell">
                    <pre>{parseDetails(row.details)}</pre>
                  </td>
                  <td className="user-agent">{row.user_agent || 'N/A'}</td>
                  <td>{formatDate(row.timestamp)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
        </>
      )}
    </div>
  );
};

export default CMS;
